import { DEAL_TYPES, type DealType } from '../deals/dealModel';
import { STALE_SEND_WARNING_DAYS_DEFAULT, STALE_SEND_WARNING_DAYS_KEY } from './staleSend';

/**
 * The ten Settings keys the accounting documents read (piece 22), each an
 * Organization-scoped SettingValue row with its default, spelled once here so
 * every reader names a declared key and the seed writes the same defaults.
 * The Settings Index entries and the editors over them are a later stage's;
 * these are the rows and the names.
 */
export const DISCOUNT_LEVEL_KEY = 'documents.discountLevel';
export const DISCOUNT_LEVELS = ['Footer', 'Line'] as const;
export type DiscountLevelValue = (typeof DISCOUNT_LEVELS)[number];

export const DISPLAYED_QUANTITY_PRECISION_KEY = 'documents.displayedQuantityPrecision';
/** Stored scale stays 2 and is never configurable; the display shows no more than that (decisions 0058, 0090). */
export const STORED_QUANTITY_SCALE = 2;

export const LABOR_MODIFIER_COMPOSITION_KEY = 'pricing.laborModifierComposition';
export const LABOR_MODIFIER_COMPOSITIONS = ['HighestSingle', 'Multiply', 'Additive'] as const;
export type LaborModifierComposition = (typeof LABOR_MODIFIER_COMPOSITIONS)[number];

/** The after-hours premium as a factor, 1.5 meaning ×1.5 (decisions 0080, 0388); the timestamp decides whether it applies and this row decides how much. */
export const AFTER_HOURS_MODIFIER_KEY = 'pricing.afterHoursModifier';
export const DEFAULT_AFTER_HOURS_MODIFIER = 1.5;

export const TARGET_MARGIN_COST_BASIS_KEY = 'pricing.targetMarginCostBasis';
/** The seed is the higher of the Item's average landed cost on hand and its current purchase price (decision 0080; product owner, 2026-09-14). */
export const TARGET_MARGIN_COST_BASES = ['HigherOfAverageLandedAndPurchase', 'AverageLandedCost', 'PurchasePrice'] as const;
export type TargetMarginCostBasis = (typeof TARGET_MARGIN_COST_BASES)[number];

export const DEFAULT_SHEET_METHOD_KEY = 'pricing.defaultSheetMethod';
export const PRICE_SHEET_METHODS = ['TargetMargin', 'PercentOffList', 'FixedPrice'] as const;
export type PriceSheetMethod = (typeof PRICE_SHEET_METHODS)[number];

export const TRAVEL_ROUNDING_INCREMENT_KEY = 'travel.roundingIncrementMinutes';
export const TRAVEL_CACHE_STALENESS_KEY = 'travel.cacheStalenessDays';
/** Currency: the value is integer cents, the shape piece 2 gives a Currency field, seeded at 10,000 dollars (settled 2026-09-14). */
export const DEFAULT_CREDIT_LIMIT_SETTING_KEY = 'credit.defaultCreditLimit';
export const DISPATCH_DEFAULT_ORIGIN_KEY = 'dispatch.defaultOriginSiteId';
/** The RFQ chase defaults (decision 0121, amended): the working days between chases and the working days to the no-response alarm, read where the RFQ leaves its own blank. */
export const RFQ_CHASE_EVERY_KEY = 'rfq.chaseEveryWorkingDays';
export const RFQ_NO_RESPONSE_ALARM_KEY = 'rfq.noResponseAlarmWorkingDays';

export const AUTO_CONVERSION_MODES = ['Off', 'CreateDraft', 'CreateAndSend'] as const;
export type AutoConversionMode = (typeof AUTO_CONVERSION_MODES)[number];
export const AUTO_CONVERSION_KEY_PREFIX = 'documents.autoConversionMode';

/**
 * The setting is per dealType, never one global value; the member folds into
 * the name — documents.autoConversionModeProject — because a key is
 * `category.name` and nothing longer (piece 2, held by a check constraint).
 */
export function autoConversionKey(dealType: DealType): string {
  return `${AUTO_CONVERSION_KEY_PREFIX}${dealType}`;
}

export interface DocumentSettingRow {
  key: string;
  /** null seeds a JSON null: the dispatch origin is named at cutover, not by the seed. */
  value: string | number | null;
}

/** The fourteen keys as the sixteen rows the piece-rows seed writes, the auto-conversion mode once per dealType. */
export const PIECE_22_SETTINGS: readonly DocumentSettingRow[] = [
  { key: DISCOUNT_LEVEL_KEY, value: 'Footer' },
  { key: DISPLAYED_QUANTITY_PRECISION_KEY, value: 2 },
  { key: LABOR_MODIFIER_COMPOSITION_KEY, value: 'HighestSingle' },
  { key: AFTER_HOURS_MODIFIER_KEY, value: DEFAULT_AFTER_HOURS_MODIFIER },
  { key: TARGET_MARGIN_COST_BASIS_KEY, value: 'HigherOfAverageLandedAndPurchase' },
  { key: DEFAULT_SHEET_METHOD_KEY, value: 'TargetMargin' },
  { key: TRAVEL_ROUNDING_INCREMENT_KEY, value: 15 },
  { key: DEFAULT_CREDIT_LIMIT_SETTING_KEY, value: 1_000_000 },
  ...DEAL_TYPES.map((dealType): DocumentSettingRow => ({ key: autoConversionKey(dealType), value: 'Off' })),
  { key: TRAVEL_CACHE_STALENESS_KEY, value: 90 },
  { key: DISPATCH_DEFAULT_ORIGIN_KEY, value: null },
  { key: RFQ_CHASE_EVERY_KEY, value: 1 },
  { key: RFQ_NO_RESPONSE_ALARM_KEY, value: 3 },
  // Decision 1227 (T421): the days a document's date may stand before today on its first send.
  { key: STALE_SEND_WARNING_DAYS_KEY, value: STALE_SEND_WARNING_DAYS_DEFAULT },
];

/** The fourteen declared keys, the auto-conversion family counted once; a reader naming a key outside this list names nothing. */
export const PIECE_22_SETTING_KEYS: readonly string[] = [
  DISCOUNT_LEVEL_KEY, DISPLAYED_QUANTITY_PRECISION_KEY, LABOR_MODIFIER_COMPOSITION_KEY, AFTER_HOURS_MODIFIER_KEY, TARGET_MARGIN_COST_BASIS_KEY, DEFAULT_SHEET_METHOD_KEY,
  TRAVEL_ROUNDING_INCREMENT_KEY, DEFAULT_CREDIT_LIMIT_SETTING_KEY, AUTO_CONVERSION_KEY_PREFIX, TRAVEL_CACHE_STALENESS_KEY, DISPATCH_DEFAULT_ORIGIN_KEY, RFQ_CHASE_EVERY_KEY, RFQ_NO_RESPONSE_ALARM_KEY,
  STALE_SEND_WARNING_DAYS_KEY,
];

export function isDeclaredDocumentSetting(key: string): boolean {
  return PIECE_22_SETTING_KEYS.includes(key) || DEAL_TYPES.some((t) => autoConversionKey(t) === key);
}

/**
 * The refusals the three Settings › Accounting values screens make before a
 * write (piece 15, the pattern; piece 22, the keys). Each is a plain sentence
 * the screen shows, and the settings write checks the same one server-side.
 */

/** The display shows no more decimals than the stored scale carries, and never a fraction of a decimal. */
export function displayedPrecisionProblem(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isInteger(value)) return 'the displayed precision is a whole number of decimals';
  if (value < 0 || value > STORED_QUANTITY_SCALE) return `the displayed precision is 0 to ${STORED_QUANTITY_SCALE} decimals; the stored scale is ${STORED_QUANTITY_SCALE} and is not configurable`;
  return null;
}

/** The discount sits in the footer or on each line, and nothing else; a blank is refused because every document reads the level. */
export function discountLevelProblem(value: unknown): string | null {
  return typeof value === 'string' && (DISCOUNT_LEVELS as readonly string[]).includes(value) ? null : `the discount scope is ${DISCOUNT_LEVELS.join(' or ')}`;
}

/** One of the three modes and nothing else; the value is per dealType, never one global choice. */
export function autoConversionModeProblem(value: unknown): string | null {
  return typeof value === 'string' && (AUTO_CONVERSION_MODES as readonly string[]).includes(value) ? null : `the conversion mode is ${AUTO_CONVERSION_MODES.join(', ')}`;
}

/** A Currency in integer cents, above zero: a blank override resolves to this number, so it is never nothing and never zero. */
export function creditLimitProblem(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isInteger(value)) return 'the default credit limit is an amount of money';
  if (value <= 0) return 'the default credit limit is above zero; a blank per-Company override resolves to it and is never no credit';
  return null;
}

export class UndeclaredSettingError extends Error {}

/**
 * The boot check a reader of these keys runs (piece 22, Settings keys suite):
 * a service names the keys it reads at construction, and a key outside the
 * declared thirteen fails at startup rather than answering undefined at the desk.
 */
export function assertDocumentSettingReads(keys: readonly string[]): void {
  const undeclared = keys.filter((key) => !isDeclaredDocumentSetting(key));
  if (undeclared.length > 0) throw new UndeclaredSettingError(`a document service reads Settings key(s) the plan does not declare: ${undeclared.join(', ')}`);
}
